import { useEffect, useState } from 'react'
import { useApiClient } from './api-client'
import type { AvatarSize } from '../api'

const preloadImage = (
  url: string | null,
  onComplete: (url: string | null) => void,
) => {
  // Gravatar URLs are returned by the backend for users without a custom avatar.
  // They don't carry signed-URL expiry and redirect through a CDN, so preloading
  // them causes CORS/redirect noise without any flicker-prevention benefit.
  if (
    !url ||
    typeof Image === 'undefined' ||
    url.toLowerCase().includes('gravatar')
  ) {
    onComplete(url)
    return
  }

  const img = new Image()
  img.onload = () => onComplete(url)
  img.onerror = () => onComplete(null)
  img.src = url
}

export const useAvatar = (userId?: string, size: AvatarSize = 'large') => {
  // useApiClient reads from React context — the reference is stable across renders.
  const apiClient = useApiClient()

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) {
      setAvatarUrl(null)
      return
    }

    // AvatarApiClient handles TTL eviction internally — getCachedAvatar returns
    // undefined once the signed URL's cache entry expires, triggering a re-fetch.
    const cached = apiClient.avatar.getCachedAvatar(userId, size)
    if (cached !== undefined) {
      setAvatarUrl(cached)
      return
    }

    setAvatarUrl(null)

    let mounted = true

    apiClient.avatar
      .getAvatars([userId], size)
      .then((result) => {
        const url = result[userId] ?? null
        preloadImage(url, (resolved) => {
          if (mounted) setAvatarUrl(resolved)
        })
      })
      .catch(() => {
        if (mounted) setAvatarUrl(null)
      })

    return () => {
      mounted = false
    }
  }, [userId, size, apiClient])

  return avatarUrl
}
